import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../../../App';

const AUTH_HREFS = ['/login', '/signin', '/signup', '/sign-in', '/sign-up', '/auth'];

/**
 * Hero block — eyebrow, headline, subheadline, primary/secondary CTA.
 * data: { eyebrow, headline, subheadline, cta_primary?: {label, href}, cta_secondary?: {...} }
 */
export default function Hero({ data = {} }) {
  const {
    eyebrow,
    headline = 'Untitled',
    subheadline,
    cta_primary,
    cta_secondary,
  } = data;
  return (
    <section className="relative overflow-hidden orka-surface-page" data-testid="site-block-hero">
      <div className="absolute inset-0 pointer-events-none opacity-60">
        <div className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full bg-violet-200/40 dark:bg-violet-700/20 blur-3xl" />
        <div className="absolute top-20 -right-32 w-[520px] h-[520px] rounded-full bg-cyan-200/40 dark:bg-cyan-700/20 blur-3xl" />
      </div>
      <div className="relative max-w-6xl mx-auto px-6 py-20 lg:py-32">
        {eyebrow && (
          <div className="text-[11px] tracking-[0.2em] uppercase font-semibold orka-text-intent mb-6" data-testid="site-block-hero-eyebrow">
            {eyebrow}
          </div>
        )}
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight max-w-4xl orka-text-primary"
          data-testid="site-block-hero-headline"
        >
          {headline}
        </h1>
        {subheadline && (
          <p
            className="mt-6 text-lg lg:text-xl orka-text-secondary max-w-2xl leading-relaxed"
            data-testid="site-block-hero-subheadline"
          >
            {subheadline}
          </p>
        )}
        {(cta_primary || cta_secondary) && (
          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="site-block-hero-ctas">
            {cta_primary?.label && (
              <CTAButton href={cta_primary.href} variant="primary" testId="site-block-hero-cta-primary">
                {cta_primary.label}
                <ArrowRight className="w-4 h-4" />
              </CTAButton>
            )}
            {cta_secondary?.label && (
              <CTAButton href={cta_secondary.href} variant="secondary" testId="site-block-hero-cta-secondary">
                {cta_secondary.label}
              </CTAButton>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function CTAButton({ href = '#', variant = 'primary', children, testId }) {
  const { login } = useAuth();
  const classes =
    variant === 'primary'
      ? 'inline-flex items-center gap-2 h-11 px-6 rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-medium text-sm orka-transition-colors hover:bg-slate-800 dark:hover:bg-white orka-focus-ring'
      : 'inline-flex items-center gap-2 h-11 px-6 rounded-full border orka-border-default orka-text-primary font-medium text-sm orka-transition-colors hover:orka-surface-subtle orka-focus-ring';
  const normalized = (href || '').toLowerCase();
  if (AUTH_HREFS.includes(normalized)) {
    return (
      <button type="button" onClick={() => login()} className={classes} data-testid={testId}>
        {children}
      </button>
    );
  }
  // Internal link if starts with `/`, anchor if `#`, else external.
  if (href?.startsWith('/')) {
    return <Link to={href} className={classes} data-testid={testId}>{children}</Link>;
  }
  return <a href={href} className={classes} data-testid={testId}>{children}</a>;
}
